import { Component, OnInit, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';

@Component({
  selector: 'app-devis',
  templateUrl: './devis.component.html',
  styleUrls: ['./devis.component.scss']
})
export class DevisComponent implements OnInit {

    currentYear = new Date().getFullYear();

  
  currentStep: number = 1;
  submissionSuccess: boolean = false;
  submissionError: string = '';
  isSubmitting: boolean = false;
  
  // Enhanced form data with more business-focused fields
  formData = {
    // Step 1: Contact Information
    name: '',
    email: '',
    company: '',
    phone: '',
    website: '',
    
    // Step 2: Project Requirements
    services: {
      webSite: false,
      seo: false,
      advertising: false,
      socialMedia: false,
      design: false,
      photo: false,
      copywriting: false,
      global: false,
      development: false,
      ecommerce: false,
      maintenance: false,
      consulting: false
    },
    
    // Step 3: Additional Information
    subject: '',
    details: '',
    acceptTerms: false
  };

  @ViewChild('devisForm') devisForm!: NgForm;

  constructor() { }

  ngOnInit(): void {
    // Scroll to top when component loads
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
  
  // Navigation methods
  nextStep(): void {
    if (this.validateCurrentStep() && this.currentStep < 3) {
      this.currentStep++;
      this.scrollToFormTop();
    }
  }
  
  previousStep(): void {
    if (this.currentStep > 1) {
      this.currentStep--;
      this.scrollToFormTop();
    }
  }

  scrollToFormTop(): void {
    setTimeout(() => {
      const formElement = document.querySelector('.devis-form-section');
      if (formElement) {
        formElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  }
  
  // Validation methods
  validateCurrentStep(): boolean {
    this.submissionError = '';
    
    switch (this.currentStep) {
      case 1:
        if (!this.formData.name.trim()) {
          this.submissionError = 'Le nom est requis.';
          return false;
        }
        if (!this.formData.email.trim() || !this.isValidEmail(this.formData.email)) {
          this.submissionError = 'Une adresse email valide est requise.';
          return false;
        }
        if (!this.formData.company.trim()) {
          this.submissionError = 'Le nom de l\'entreprise est requis.';
          return false;
        }
        break;
      
      case 2:
        if (!this.hasSelectedServices()) {
          this.submissionError = 'Veuillez sélectionner au moins un service.';
          return false;
        }
        break;

      case 3:
        if (!this.formData.subject.trim()) {
          this.submissionError = 'Le sujet est requis.';
          return false;
        }
        if (!this.formData.acceptTerms) {
          this.submissionError = 'Veuillez accepter les conditions générales.';
          return false;
        }
        break;
    }
    
    return true;
  }
  
  isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }
  
  hasSelectedServices(): boolean {
    return Object.values(this.formData.services).some(value => value === true);
  }

  getSelectedServicesCount(): number {
    return Object.values(this.formData.services).filter(value => value === true).length;
  }
  
  // Form submission
  async onSubmit(): Promise<void> {
    if (!this.validateCurrentStep()) {
      return;
    }
    
    this.isSubmitting = true;
    this.submissionError = '';
    
    try {
      await this.submitFormToWeb3Forms();
      this.submissionSuccess = true;
      
      // Scroll to success message
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
      
    } catch (error) {
      console.error('Form submission error:', error);
      this.submissionError = 'Une erreur est survenue lors de l\'envoi de votre demande. Veuillez réessayer ou nous contacter directement.';
    } finally {
      this.isSubmitting = false;
    }
  }

  // Submit to Web3Forms
  private async submitFormToWeb3Forms(): Promise<any> {
    return new Promise((resolve, reject) => {
      const formDataToSend = new FormData();
      
      // Web3Forms access key
      formDataToSend.append('access_key', 'ed3bd6d3-e198-4b84-b8cf-7b59f9ea2aba');
      
      // Basic information
      formDataToSend.append('name', this.formData.name);
      formDataToSend.append('email', this.formData.email);
      formDataToSend.append('company', this.formData.company);
      formDataToSend.append('phone', this.formData.phone || 'Non spécifié');
      formDataToSend.append('website', this.formData.website || 'Non spécifié');
      
      // Project information
      const selectedServices = Object.entries(this.formData.services)
        .filter(([_, value]) => value)
        .map(([key, _]) => {
          const serviceNames: { [key: string]: string } = {
            webSite: 'Création de site web',
            seo: 'Référencement SEO',
            advertising: 'Publicité en ligne',
            socialMedia: 'Gestion des réseaux sociaux',
            design: 'Design graphique',
            photo: 'Photographie / Vidéographie',
            copywriting: 'Copywriting',
            global: 'Accompagnement global',
            development: 'Développement d\'application',
            ecommerce: 'E-commerce',
            maintenance: 'Maintenance',
            consulting: 'Conseil stratégique'
          };
          return serviceNames[key] || key;
        })
        .join(', ');
      
      formDataToSend.append('services', selectedServices);
      
      // Additional information
      formDataToSend.append('subject', this.formData.subject);
      formDataToSend.append('details', this.formData.details || 'Aucun détail supplémentaire');
      
      // Create comprehensive message
      const comprehensiveMessage = `
NOUVELLE DEMANDE DE DEVIS

═══════════════════════════════════════
📋 INFORMATIONS DE CONTACT
═══════════════════════════════════════
Nom: ${this.formData.name}
Email: ${this.formData.email}
Entreprise: ${this.formData.company}
Téléphone: ${this.formData.phone || 'Non spécifié'}
Site web: ${this.formData.website || 'Non spécifié'}

═══════════════════════════════════════
🎯 DÉTAILS DU PROJET
═══════════════════════════════════════
Sujet: ${this.formData.subject}

Services demandés:
${selectedServices || 'Aucun service sélectionné'}

Détails du projet:
${this.formData.details || 'Aucun détail supplémentaire'}

═══════════════════════════════════════
ℹ️ MÉTADONNÉES
═══════════════════════════════════════
Date de soumission: ${new Date().toLocaleString('fr-FR')}
Conditions acceptées: Oui
      `.trim();
      
      formDataToSend.append('message', comprehensiveMessage);
      formDataToSend.append('subject', `Demande de Devis - ${this.formData.company} - ${selectedServices.substring(0, 50)}${selectedServices.length > 50 ? '...' : ''}`);
      
      // Send to Web3Forms
      fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        body: formDataToSend
      })
      .then(response => response.json())
      .then(data => {
        if (data.success) {
          resolve({ success: true, message: 'Demande envoyée avec succès!' });
        } else {
          reject(new Error('Erreur lors de l\'envoi'));
        }
      })
      .catch(error => reject(error));
    });
  }
  
  // Reset form
  resetForm(): void {
    if (this.devisForm) {
      this.devisForm.resetForm();
    }
    
    this.formData = {
      name: '',
      email: '',
      company: '',
      phone: '',
      website: '',
      services: {
        webSite: false,
        seo: false,
        advertising: false,
        socialMedia: false,
        design: false,
        photo: false,
        copywriting: false,
        global: false,
        development: false,
        ecommerce: false,
        maintenance: false,
        consulting: false
      },
      subject: '',
      details: '',
      acceptTerms: false
    };
    
    this.currentStep = 1;
    this.submissionSuccess = false;
    this.submissionError = '';
    this.isSubmitting = false;
    
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  // Utility method to scroll to a section
  scrollToSection(sectionClass: string): void {
    const element = document.querySelector('.' + sectionClass);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }
}